// @src/pre-deployment/src/components/ProfileComplete.js v1.0
// - Profile completion page for pre-deployment environment
// - Shows assessment results and provides access to main application
// - Completes the registration flow

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthContext';

const ProfileComplete = () => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [username, setUsername] = useState('');
  
  const { user, userProfile, completeProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const handleCompleteProfile = useCallback(async () => {
    console.group('✅ PRE-DEPLOYMENT PROFILE COMPLETE: Completing profile');
    setIsLoading(true);
    
    const result = await completeProfile();
    
    if (result.success) {
      console.log('Profile completed successfully');
      setMessage('Welcome! Your profile is now complete.');
    } else {
      console.error('Profile completion failed:', result.error);
      setMessage(`Error: ${result.error}`);
    }
    
    setIsLoading(false);
    console.groupEnd();
  }, [completeProfile]);

  useEffect(() => {
    const checkProfileState = async () => {
      console.group('✅ PRE-DEPLOYMENT PROFILE COMPLETE: Component mounted');
      console.log('User:', user?.uid);
      console.log('User profile:', userProfile);
      
      // If we have inconsistent data, refresh from Firestore
      if (user && userProfile && (!userProfile.assessmentComplete || !userProfile.passwordSet)) {
        console.log('Detected potentially stale data, refreshing profile...');
        const freshProfile = await refreshProfile();
        
        if (freshProfile) {
          console.log('Profile refreshed:', freshProfile);
          console.groupEnd();
          return;
        }
      }
      
      console.groupEnd();

      // Redirect if no authenticated user
      if (!user) {
        console.log('No authenticated user, redirecting to home');
        navigate('/');
        return;
      }
      
      // If no profile data yet, wait for it to load
      if (!userProfile) {
        console.log('Waiting for user profile to load...');
        return;
      }
      
      // Check assessment completion
      if (!userProfile.assessmentComplete) {
        console.log('Assessment not completed, redirecting to assessment');
        navigate('/assessment');
        return;
      }
      
      // Auto-complete profile if we got here and it's not complete
      if (!userProfile.profileComplete) {
        console.log('Auto-completing profile...');
        handleCompleteProfile();
      }
    };
    
    checkProfileState();
  }, [user, userProfile, navigate, refreshProfile, handleCompleteProfile]);

  const getLevelDescription = (level) => {
    const descriptions = {
      0: {
        title: "Complete Beginner",
        description: "Perfect starting point! You'll learn hangul and basic Korean fundamentals from the ground up."
      },
      1: {
        title: "Beginner Level",
        description: "You're just starting your Korean journey! Focus on basic vocabulary, hangul reading, and simple phrases."
      },
      2: {
        title: "Elementary Level", 
        description: "You know some basics! Continue building vocabulary and start learning basic grammar patterns."
      },
      3: {
        title: "Pre-Intermediate Level",
        description: "Good progress! You can handle simple conversations and understand basic Korean structure."
      },
      4: {
        title: "Intermediate Level",
        description: "Well done! You can communicate in various situations and understand more complex grammar."
      },
      5: {
        title: "Upper-Intermediate Level",
        description: "Excellent! You can express yourself fluently and understand most Korean content."
      },
      6: {
        title: "Advanced Level",
        description: "Outstanding! You have near-native proficiency and can handle complex Korean in all contexts."
      }
    };
    
    return descriptions[level] || descriptions[1];
  };

  const handleStartLearning = async () => {
    console.log('🚀 PRE-DEPLOYMENT PROFILE COMPLETE: Starting learning journey');
    
    setIsLoading(true);
    try {
      // Save username to profile if provided
      if (username.trim()) {
        console.log('Saving username:', username);
        await completeProfile({ username: username.trim() });
      }
      
      navigate('/app-homepage');
    } catch (error) {
      console.error('Error saving username:', error);
      setMessage('Error saving username. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate('/assessment');
  };

  // Show loading state while checking profile
  if (!user || !userProfile) {
    return (
      <div className="container">
        <div className="logo-section">
          <img src="/img/logo.png" alt="Logo" className="logo" />
          <h1>Pre-Deployment</h1>
          <p>Loading...</p>
        </div>
        
        <div className="auth-section">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  const assessment = userProfile.assessment || {};
  const finalLevel = assessment.finalLevel || 0;
  const levelInfo = getLevelDescription(finalLevel);

  return (
    <div className="container">
      <div className="logo-section">
        <img src="/img/logo.png" alt="Logo" className="logo" />
        <h1>Pre-Deployment</h1>
        <p>Profile Complete</p>
        
        <div className="username-section">
          <h3>Choose Your Username</h3>
          <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '15px' }}>
            This will be displayed in your learning profile.
          </p>
          <div className="input-group">
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              className="username-input"
              maxLength={20}
              disabled={isLoading}
            />
            <div className="input-hint">
              {username.length}/20 characters
            </div>
          </div>
        </div>
      </div>
      
      <div className="auth-section">
        <h2>🎉 Registration Complete!</h2>
        
        <div className="profile-info">
          <div className="info-item">
            <strong>Email:</strong> {user.email}
          </div>
          <div className="info-item">
            <strong>Registration Date:</strong> {new Date(userProfile.createdAt?.toDate ? userProfile.createdAt.toDate() : userProfile.createdAt).toLocaleDateString()}
          </div>
          <div className="info-item">
            <strong>Assessment Completed:</strong> ✅ Yes
          </div>
        </div>

        <div className="level-description">
          <h3>Your Korean Level: Level {finalLevel}</h3>
          <h4>{levelInfo.title}</h4>
          <p>{levelInfo.description}</p>
        </div>

        {assessment.levelScores && Object.keys(assessment.levelScores).length > 0 && (
          <div className="profile-info">
            <h3>Assessment Results</h3>
            {Object.entries(assessment.levelScores).map(([level, score]) => (
              <div key={level} className="info-item">
                <strong>Level {level}:</strong> {Math.round(score)}%
              </div>
            ))}
          </div>
        )}

        <div className="navigation-controls">
          <button className="nav-btn secondary" onClick={handleGoBack}>
            <i className="fas fa-arrow-left"></i>
            Back to Assessment
          </button>
          
          <button className="nav-btn primary" onClick={handleStartLearning} disabled={isLoading}>
            <i className="fas fa-play"></i>
            {isLoading ? 'Setting up...' : 'Start Learning'}
          </button>
        </div>

        {message && (
          <div className={`message ${message.includes('Welcome') ? 'success' : 'error'}`}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfileComplete;
